import type { DbExecutor } from "./executor";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import { ValidationError } from "./repositories/projectRepository";
import { generateId } from "./ids";
import {
  rankEvidence,
  summarizeCoverage,
  toCents,
  type EstimateRecommendation,
  type EvidenceCoverage,
} from "../engine/estimateEvidence";
import type {
  CostEvidence,
  EstimateRequest,
  EstimateSubject,
  EvidenceKind,
  EvidenceProvider,
} from "../estimating/evidence";
import { futureEvidenceProviders } from "../estimating/providers";
import type { Cents } from "../domain/money";

/**
 * Estimate evidence service (Phase 9E, spec §26D).
 *
 * Orchestrates the evidence providers, ranks their answers with the pure
 * engine, and records what the USER decided. Three rules it exists to
 * enforce:
 *
 *   ESTIMATES MAY BE SMART      — history and live offers can lead.
 *   THEIR SOURCE IS EXPLAINABLE — every number keeps its observations.
 *   THE USER MAY OVERRIDE       — nothing is applied without an explicit act.
 *
 * Applying evidence writes through the EXISTING assembly engine (the
 * component's unit cost), so generated lines, the BOM, procurement and
 * Phase 9C reconciliation all follow with no parallel pricing path.
 */

export interface EvidenceProviderStatus {
  id: string;
  label: string;
  kind: EvidenceKind;
  available: boolean;
  note: string | null;
  /** How many pieces of evidence it contributed this time. */
  contributed: number;
}

export interface EvidenceDecisionRecord {
  id: string;
  componentId: string;
  projectId: string;
  decision: "accepted" | "alternative" | "manual" | "kept";
  chosenKind: EvidenceKind | null;
  chosenProvider: string | null;
  chosenUnitX100: number;
  recommendedKind: EvidenceKind | null;
  recommendedUnitX100: number | null;
  sampleSize: number;
  evidenceAsOf: string | null;
  rationale: string[];
  note: string;
  decidedAt: string;
}

export interface EvidenceReview {
  subject: EstimateSubject;
  request: EstimateRequest;
  evidence: CostEvidence[];
  recommendation: EstimateRecommendation;
  providers: EvidenceProviderStatus[];
  /** The price the estimate is using right now. */
  currentUnitCostCents: Cents;
  /** True when the current price comes from a duration assumption (§20A). */
  currentIsDuration: boolean;
  /** True when a selected product governs this line's price (Phase 6). */
  productGoverns: boolean;
  decision: EvidenceDecisionRecord | null;
}

export class EstimateEvidenceService {
  private readonly providers: EvidenceProvider[];

  constructor(
    private readonly db: DbExecutor,
    private readonly assemblies: AssemblyRepository,
    providers: EvidenceProvider[],
  ) {
    // Declared-but-unbuilt sources are registered too, so the panel can say
    // what does not exist yet instead of silently omitting it.
    this.providers = [...providers, ...futureEvidenceProviders()];
  }

  /** What is being priced, resolved from the assembly component. */
  async subjectFor(componentId: string): Promise<{ subject: EstimateSubject; projectId: string; zip: string; quantityX100: number | null; currentUnitCostCents: Cents; currentIsDuration: boolean; productGoverns: boolean; instanceId: string }> {
    const rows = await this.db.select<SubjectRow>(
      `SELECT c.id, c.name, c.unit, c.unit_cost_cents, c.labor_minutes_x100, c.component_type,
              c.material_definition_id, c.instance_id,
              f.feature_definition_id, f.name AS feature_name, f.project_id,
              p.zip,
              s.product_id AS selected_product_id
         FROM assembly_instance_components c
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN project_features f ON f.id = i.project_feature_id
         JOIN projects p ON p.id = f.project_id
         LEFT JOIN project_product_selections s ON s.component_id = c.id AND s.replaced_at IS NULL
        WHERE c.id = ?`,
      [componentId],
    );
    if (rows.length === 0) throw new ValidationError(["This estimate line no longer exists."]);
    const row = rows[0];
    const calculation = await this.assemblies.calculate(row.instance_id).catch(() => null);
    const componentCalc = calculation?.components.find((c) => c.componentId === componentId) ?? null;
    return {
      instanceId: row.instance_id,
      projectId: row.project_id,
      zip: row.zip ?? "",
      quantityX100: componentCalc ? componentCalc.effectiveQtyX100 : null,
      currentUnitCostCents: Number(row.unit_cost_cents),
      currentIsDuration: row.labor_minutes_x100 !== null,
      productGoverns: row.selected_product_id !== null,
      subject: {
        kind: "component",
        featureDefinitionId: row.feature_definition_id,
        componentName: row.name,
        costBasis: costBasisFor(row.component_type),
        unit: row.unit,
        materialDefinitionId: row.material_definition_id,
        productId: row.selected_product_id,
        label: `${row.feature_name} — ${row.name}`,
      },
    };
  }

  /** Gather, rank and explain. Reads only; nothing is applied. */
  async review(componentId: string): Promise<EvidenceReview> {
    const resolved = await this.subjectFor(componentId);
    const request: EstimateRequest = {
      subject: resolved.subject,
      market: { zip: resolved.zip, label: resolved.zip ? `ZIP ${resolved.zip}` : "no market set" },
      projectId: resolved.projectId,
      quantityX100: resolved.quantityX100,
      asOf: new Date().toISOString().slice(0, 10),
    };

    const evidence: CostEvidence[] = [];
    const providers: EvidenceProviderStatus[] = [];
    for (const provider of this.providers) {
      const availability = await provider.availability();
      let contributed: CostEvidence[] = [];
      if (availability.available) {
        try {
          contributed = await provider.gather(request);
        } catch (err) {
          console.error(`Evidence provider ${provider.id} failed:`, err);
          providers.push({
            id: provider.id, label: provider.label, kind: provider.kind,
            available: false, note: "This source could not be read just now.", contributed: 0,
          });
          continue;
        }
      }
      evidence.push(...contributed);
      providers.push({
        id: provider.id, label: provider.label, kind: provider.kind,
        available: availability.available, note: availability.note, contributed: contributed.length,
      });
    }

    return {
      subject: resolved.subject,
      request,
      evidence,
      recommendation: rankEvidence(evidence, request),
      providers,
      currentUnitCostCents: resolved.currentUnitCostCents,
      currentIsDuration: resolved.currentIsDuration,
      productGoverns: resolved.productGoverns,
      decision: await this.decisionFor(componentId),
    };
  }

  /**
   * Apply a decision to the estimate. `unitCostCentsX100` is what the user
   * settled on — the recommendation, an alternative, or their own number.
   *
   * Writes through the assembly engine so generated lines regenerate the
   * normal way; a duration-based labor component becomes dollar-priced,
   * which the caller is told about before it happens.
   */
  async apply(
    componentId: string,
    input: {
      decision: "accepted" | "alternative" | "manual";
      unitCostCentsX100: number;
      evidence: CostEvidence | null;
      recommendation: EstimateRecommendation;
      note?: string;
    },
  ): Promise<void> {
    if (!Number.isFinite(input.unitCostCentsX100) || input.unitCostCentsX100 < 0) {
      throw new ValidationError(["A unit cost must be a valid non-negative amount."]);
    }
    const resolved = await this.subjectFor(componentId);
    if (resolved.productGoverns) {
      throw new ValidationError([
        "A selected product prices this line. Change the product (or keep it) in Materials — evidence never overrides a product you chose.",
      ]);
    }
    const component = (await this.assemblies.getInstance(resolved.instanceId))?.components.find((c) => c.id === componentId);
    if (!component) throw new ValidationError(["This estimate line no longer exists."]);

    await this.db.transaction(async () => {
      await this.assemblies.updateComponent(resolved.instanceId, componentId, {
        name: component.name,
        componentType: component.componentType,
        role: component.role,
        inclusion: component.inclusion,
        conditionOptionId: component.conditionOptionId,
        qtyBasis: component.qtyBasis,
        fixedQtyX100: component.fixedQtyX100,
        multiplierX10000: component.multiplierX10000,
        wastePctX100: component.wastePctX100,
        unit: component.unit,
        unitCostCents: toCents(input.unitCostCentsX100),
        // Evidence is a dollar figure: it replaces a duration assumption
        // rather than silently coexisting with one (§20A).
        laborMinutesX100: null,
        taxable: component.taxable,
        categoryId: component.categoryId,
        notes: component.notes,
      });
      await this.recordDecision(componentId, resolved, input);
    });
  }

  /** Record "I looked and I am keeping what is there" — a real answer. */
  async keep(componentId: string, recommendation: EstimateRecommendation, note = ""): Promise<void> {
    const resolved = await this.subjectFor(componentId);
    await this.recordDecision(componentId, resolved, {
      decision: "kept",
      unitCostCentsX100: resolved.currentUnitCostCents * 100,
      evidence: null,
      recommendation,
      note,
    });
  }

  async clearDecision(componentId: string): Promise<void> {
    await this.db.execute("DELETE FROM estimate_evidence_decisions WHERE component_id = ?", [componentId]);
  }

  private async recordDecision(
    componentId: string,
    resolved: Awaited<ReturnType<EstimateEvidenceService["subjectFor"]>>,
    input: {
      decision: "accepted" | "alternative" | "manual" | "kept";
      unitCostCentsX100: number;
      evidence: CostEvidence | null;
      recommendation: EstimateRecommendation;
      note?: string;
    },
  ): Promise<void> {
    const now = new Date().toISOString();
    const recommended = input.recommendation.chosen;
    await this.db.execute("DELETE FROM estimate_evidence_decisions WHERE component_id = ?", [componentId]);
    await this.db.execute(
      `INSERT INTO estimate_evidence_decisions (
         id, project_id, component_id, feature_definition_id, component_name, cost_basis, unit,
         market_zip, recommended_kind, recommended_provider, recommended_unit_x100,
         decision, chosen_kind, chosen_provider, chosen_unit_x100, sample_size,
         low_unit_x100, high_unit_x100, evidence_as_of, rationale_json, note,
         decided_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        generateId(), resolved.projectId, componentId, resolved.subject.featureDefinitionId,
        resolved.subject.componentName ?? "", resolved.subject.costBasis, resolved.subject.unit,
        resolved.zip,
        recommended?.kind ?? null, recommended?.providerId ?? null,
        recommended ? recommended.unitCostCentsX100 : null,
        input.decision,
        input.evidence?.kind ?? (input.decision === "manual" ? "manual-override" : null),
        input.evidence?.providerId ?? null,
        Math.round(input.unitCostCentsX100),
        input.evidence?.sampleSize ?? 0,
        input.evidence?.lowCentsX100 ?? null,
        input.evidence?.highCentsX100 ?? null,
        input.evidence?.asOf ?? null,
        JSON.stringify(input.recommendation.rationale),
        input.note ?? "",
        now, now, now,
      ],
    );
  }

  async decisionFor(componentId: string): Promise<EvidenceDecisionRecord | null> {
    const rows = await this.db.select<DecisionRow>(
      "SELECT * FROM estimate_evidence_decisions WHERE component_id = ?",
      [componentId],
    );
    return rows[0] ? toDecision(rows[0]) : null;
  }

  async decisionsForProject(projectId: string): Promise<Map<string, EvidenceDecisionRecord>> {
    const rows = await this.db.select<DecisionRow>(
      "SELECT * FROM estimate_evidence_decisions WHERE project_id = ?",
      [projectId],
    );
    return new Map(rows.map((r) => [String(r.component_id), toDecision(r)]));
  }

  /**
   * How much of a project's estimate rests on evidence rather than
   * assumption — shown beside the total, so "smart" never hides "guessed".
   */
  async coverage(projectId: string, designId: string | null | undefined): Promise<EvidenceCoverage> {
    const params: unknown[] = [projectId];
    let designFilter = "";
    if (designId !== undefined) {
      designFilter = designId === null ? " AND f.design_id IS NULL" : " AND (f.design_id = ? OR f.design_id IS NULL)";
      if (designId !== null) params.push(designId);
    }
    const rows = await this.db.select<{ component_id: string; product_id: string | null }>(
      `SELECT c.id AS component_id, s.product_id
         FROM assembly_instance_components c
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN project_features f ON f.id = i.project_feature_id
         LEFT JOIN project_product_selections s ON s.component_id = c.id AND s.replaced_at IS NULL
        WHERE f.project_id = ? AND c.archived_at IS NULL AND f.archived_at IS NULL${designFilter}`,
      params,
    );
    const decisions = await this.decisionsForProject(projectId);
    const kinds: (EvidenceKind | null)[] = rows.map((row) => {
      const decision = decisions.get(row.component_id);
      if (decision && decision.decision !== "kept") return decision.chosenKind;
      // A product the user selected IS current offer evidence.
      if (row.product_id !== null) return "product-offer";
      return "template-assumption";
    });
    return summarizeCoverage(kinds);
  }
}

function costBasisFor(componentType: string): "material" | "labor" | "other" {
  if (componentType === "labor") return "labor";
  if (componentType === "material") return "material";
  return "other";
}

interface SubjectRow {
  id: string;
  name: string;
  unit: string;
  unit_cost_cents: number;
  labor_minutes_x100: number | null;
  component_type: string;
  material_definition_id: string | null;
  instance_id: string;
  feature_definition_id: string | null;
  feature_name: string;
  project_id: string;
  zip: string | null;
  selected_product_id: string | null;
}

interface DecisionRow {
  id: string;
  component_id: string;
  project_id: string;
  decision: string;
  chosen_kind: string | null;
  chosen_provider: string | null;
  chosen_unit_x100: number;
  recommended_kind: string | null;
  recommended_unit_x100: number | null;
  sample_size: number;
  evidence_as_of: string | null;
  rationale_json: string;
  note: string;
  decided_at: string;
}

function toDecision(row: DecisionRow): EvidenceDecisionRecord {
  let rationale: string[] = [];
  try {
    const parsed = JSON.parse(row.rationale_json) as unknown;
    if (Array.isArray(parsed)) rationale = parsed.filter((x): x is string => typeof x === "string");
  } catch {
    rationale = [];
  }
  return {
    id: String(row.id),
    componentId: String(row.component_id),
    projectId: String(row.project_id),
    decision: row.decision as EvidenceDecisionRecord["decision"],
    chosenKind: (row.chosen_kind as EvidenceKind | null) ?? null,
    chosenProvider: row.chosen_provider,
    chosenUnitX100: Number(row.chosen_unit_x100),
    recommendedKind: (row.recommended_kind as EvidenceKind | null) ?? null,
    recommendedUnitX100: row.recommended_unit_x100 === null ? null : Number(row.recommended_unit_x100),
    sampleSize: Number(row.sample_size ?? 0),
    evidenceAsOf: row.evidence_as_of,
    rationale,
    note: row.note ?? "",
    decidedAt: row.decided_at,
  };
}
